import { inflate } from 'pako';
import type { SqlQueryResultsWithCols } from '@/services/types';
import { graphql } from '@/utils/graphql';
import { waitUsingPerformanceTestConfigMode } from '@/utils/beemTools';

export function decompressIfNeededThenJsonParse(rows: string): unknown {
  try {
    return JSON.parse(rows);
  } catch (e) {
    const compressedRows = Uint8Array.from(atob(rows), (c) => c.charCodeAt(0));
    const decompressedRows = inflate(compressedRows, { to: 'string' });
    return JSON.parse(decompressedRows);
  }
}

const GRAPHQL_POLL_TIMEOUT_MS = 60_000;

/**
 * Wraps a promise with a timeout guard. If the promise does not settle within
 * `ms` milliseconds, it rejects with a timeout error.
 *
 * Why this exists: the polling loop below issues repeated graphql requests to
 * check query status. On weak machines or when the browser throttles a
 * background tab, a fetch request can hang indefinitely (no resolve, no reject).
 * Without a timeout the saga blocks forever, the concurrency-limiter slot is
 * never released, and the dashboard widget stays stuck in "Querying..." state
 * permanently. The timeout ensures the promise always settles, letting the
 * polling loop either retry on the next iteration or propagate the error up to
 * the saga's catch block where an error result is dispatched to Redux.
 */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('GraphQL poll request timed out')), ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
}

/**
 * Polls beemAgentCheckRedshiftQuery until the query finishes, returning both the
 * parsed rows and the parsed column metadata (`cols`) that beem-agent includes on
 * a FINISHED response. Callers that need column metadata (e.g. dashboard viz
 * widgets) use this so they can read column names/types directly instead of
 * re-discovering them with a separate Redshift round-trip.
 */
export async function noLint_waitForRedshiftQueryUntilDoneAndReturnResults(query: {
  executionId: string;
  /**
   * Opaque cache context from beemAgentPreviewDatasetQuery. When present, beem-agent
   * writes the finished result to the dashboard query cache. Absent = no caching.
   */
  cacheContext?: string;
}): Promise<SqlQueryResultsWithCols> {
  const MAX_TIME = Date.now() + 60 * 60 * 1000;
  const START_WAIT_TIME = Date.now() + 30 * 1000;
  let i = 0;
  while (Date.now() < MAX_TIME) {
    await waitUsingPerformanceTestConfigMode(i, START_WAIT_TIME);
    i += 1;
    const { status, cols, rows, error } = await withTimeout(
      graphql('beemAgentCheckRedshiftQuery', { query }),
      GRAPHQL_POLL_TIMEOUT_MS,
    );
    if (rows) {
      // Completion is signalled by `rows`. beem-agent always includes `cols` alongside
      // `rows` on a FINISHED response, so rows-without-cols is a contract violation —
      // fail loudly rather than looping until the 1-hour timeout (which is what gating
      // the return on `cols` used to cause) or silently returning empty column metadata.
      if (!cols) {
        throw new Error(
          `beemAgentCheckRedshiftQuery returned rows without cols for executionId ${query.executionId}`,
        );
      }
      return {
        cols: decompressIfNeededThenJsonParse(cols) as SqlQueryResultsWithCols['cols'],
        rows: decompressIfNeededThenJsonParse(rows) as SqlQueryResultsWithCols['rows'],
      };
    }
    if (status === 'FAILED') throw new Error(error);
    if (status === 'ABORTED')
      throw new Error(`The query was aborted. Please check query ${query}.`);
  }
  throw new Error(`The query exceeded the maximum of ${MAX_TIME} minutes.`);
}
